import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IComando,
  IConfigPerfil,
  IGeoJSONPoint,
  ILuminaria,
  IPopulate,
  IPuesta,
  IQueryParam,
} from 'modelos/src';
import { Subscription, firstValueFrom } from 'rxjs';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { LuminariasHttpService } from '../../../../auxiliares/servicios/http/luminarias.service';
import { ExportarLuminariasDialogComponent } from '../../../entidades/luminarias/luminarias/exportar-luminarias-dialog/exportar-luminarias-dialog.component';
import { DescargasService } from '../../../navigation/descargas/service';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { filtrarComandosManuales } from '../../../../auxiliares/comandos-manuales.util';
import { PuestasService } from '../../../../auxiliares/servicios/http/puestas.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { LuminariasService } from '../../../entidades/luminarias/service';
import {
  ComandosObjetivoService,
  Objetivo,
} from '../../../../auxiliares/servicios/comandos-objetivo.service';
import { CrearEditarPuestaComponent } from '../crear-editar-puesta/crear-editar-puesta.component';
import { Router } from '@angular/router';
import {
  calcularEstado,
  presentacionEstado,
  CodigoEstadoLuminaria,
  fechaUltimaComunicacion,
  estadoLuminariaCelda,
} from '../../../entidades/luminarias/estado/estado-luminaria';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';
import {
  estadoHtml,
  fechaCelda,
  haceCelda,
} from '../../../../auxiliares/tabla/celdas';

/** Hasta 2 decimales, sin «,00» en los enteros. */
function numero(n: number): string {
  return n.toLocaleString('es-AR', { maximumFractionDigits: 2 });
}

@Component({
  imports: [AuxiliaresModule],
  selector: 'app-listado-puestas',
  templateUrl: './listado-puestas.component.html',
  styleUrl: './listado-puestas.component.scss',
})
export class ListadoPuestasComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private dialogService = inject(DialogService);
  private service = inject(PuestasService);
  private lista2 = inject(Lista2Service);
  private formularios = inject(FormularioDialogService);
  private router = inject(Router);
  private comandos = inject(ComandosObjetivoService);
  private luminariasHttp = inject(LuminariasHttpService);
  private descargas = inject(DescargasService);
  private luminariasService = inject(LuminariasService);

  public name = 'ListadoPuestasComponent';
  public datos?: IPuesta[];
  public totalCount = 0;
  public columnas?: IColumnas<IPuesta>[];
  public puestas$?: Subscription;

  private readonly TIPO_GPE = 'Luminaria GPE';
  private readonly TIPO_ACTIS = 'Luminaria ACTIS FING';
  private readonly TIPO_WELLNESS = 'Luminaria Wellness';

  public search: IRegExpSearch = {
    fields: ['identificacion', 'direccion'],
  };

  // FILTROS
  //Cliente
  private clienteFiltro: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
    filtroSelector: ['moduloLuminarias'],
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  private ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };

  //Grupo de puesta
  private grupoPuesta: IFiltroTabla = {
    elementos: [],
    filter: {
      field: 'idsGrupos',
    },
    label: 'Grupo de puesta',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'ngselect-async',
    asyncFunction: 'gruposPuesta',
    searchOn: ['nombre'],
  };

  // Perfil propio (perfiles asignados directamente a la puesta)
  private perfilPropio: IFiltroTabla = {
    elementos: [
      {
        label: 'Con perfil',
        filter: [{ idsPerfilConfig: { $exists: true, $ne: [] } }],
      },
      {
        label: 'Sin perfil',
        filter: [
          { idsPerfilConfig: { $exists: false } },
          { idsPerfilConfig: { $size: 0 } },
        ],
      },
    ],
    filter: {
      field: '$or',
    },
    label: 'Perfil propio',
    rapido: true,
    tipo: 'ngselect',
    selectLabel: 'label',
    selectValue: 'filter',
  };

  // Ubicación (georreferencia propia de la puesta)
  private ubicacion: IFiltroTabla = {
    elementos: [
      {
        label: 'Con ubicación',
        filter: [{ 'ubicacion.coordinates': { $exists: true, $ne: [] } }],
      },
      {
        label: 'Sin ubicación',
        filter: [
          { 'ubicacion.coordinates': { $exists: false } },
          { 'ubicacion.coordinates': { $size: 0 } },
        ],
      },
    ],
    filter: {
      field: '$or',
    },
    label: 'Ubicación',
    rapido: true,
    tipo: 'ngselect',
    selectLabel: 'label',
    selectValue: 'filter',
  };

  // Fecha de alta (rango sobre fechaCreacion)
  private fechaAlta: IFiltroTabla = {
    filter: {
      field: 'fechaCreacion',
    },
    label: 'Fecha de alta',
    tipo: 'dateRange',
  };

  public filtros: IFiltroTabla[] =
    HelperService.getTipoCliente() !== 'Final'
      ? [
          this.clienteFiltro,
          this.ancestro,
          this.grupoPuesta,
          this.perfilPropio,
          this.ubicacion,
          this.fechaAlta,
        ]
      : [this.grupoPuesta, this.perfilPropio, this.ubicacion, this.fechaAlta];

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Luminarias - Crear puestas'),
    tooltip: 'Crear puesta',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  public populate: IPopulate[] = [
    { path: 'cliente', select: 'nombre' },
    { path: 'perfilConfigs', select: 'nombre valores tipoConfig' },
    // Grupos de puesta (para la columna Asignaciones).
    { path: 'grupos', select: 'nombre' },
    // Luminarias con su tipo y perfil propio (para comandos por tipo + modoMixto),
    // más el estado precalculado y los últimos reportes para los agregados de la
    // puesta (resumen de estados + eléctrico total + última comunicación / comando /
    // horas de encendido / asignaciones).
    {
      path: 'luminarias',
      select:
        'idPerfilConfig dispositivo deveui identificacion estado ultimoReportePeriodico ultimoReporteEnergia fechaUltimaComunicacion',
      populate: {
        path: 'dispositivo',
        select: 'tipo name fechaUltimaComunicacion ultimoComando',
      },
    },
  ];
  public query: IQueryParam = {
    populate: JSON.stringify(this.populate),
    sort: 'identificacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
  };

  public async listar(): Promise<void> {
    this.puestas$?.unsubscribe();
    this.puestas$ = this.lista2.puestas(this.query, 30).subscribe((data) => {
      this.totalCount = data.totalCount;
      this.datos = data.datos;
    });
  }

  // ── Objetivo para el servicio de comandos ───────────────────────────────
  private objetivo(p: IPuesta): Objetivo {
    return { id: p._id!, nivel: 'puesta', nombre: p.identificacion };
  }

  // ¿La puesta tiene un perfil del tipo dado? Si lo tiene, sus luminarias de ese
  // tipo quedan gobernadas por el perfil → se ocultan los comandos SET de ese
  // tipo a nivel puesta.
  public puestaTienePerfilTipo(p: IPuesta, tipo: string): boolean {
    const tipoConfig =
      tipo === this.TIPO_ACTIS ? 'Luminaria ACTIS FING General' : tipo;
    return (p.perfilConfigs || []).some((pc) => pc?.tipoConfig === tipoConfig);
  }

  // Render de los perfiles asignados directamente (propios) a la puesta: el
  // ícono de perfil y, al lado, el tipo de configuración.
  public htmlPerfilPropio(perfiles?: IConfigPerfil[] | null): string {
    if (!perfiles?.length) return '';
    let html = `<div class="tabla-celda-col">`;
    for (const p of perfiles) {
      const tipo = p?.tipoConfig || '';
      html += `<div class="tabla-linea" title="${escaparHtml(tipo)}">`;
      html += `<span class="material-symbols-outlined">tune</span>`;
      html += p?.nombre
        ? `<span>${escaparHtml(p.nombre)}</span>`
        : `<span class="no-info">Sin nombre</span>`;
      if (tipo) {
        html += `<span class="tabla-secundario">${escaparHtml(tipo)}</span>`;
      }
      html += `</div>`;
    }
    html += `</div>`;
    return html;
  }

  // Resumen de estados de las luminarias de la puesta. Reusa el estado de 6
  // códigos precalculado por el backend (`calcularEstado` lee luminaria.estado),
  // así que no recalcula nada en el front. Muestra el total y el desglose por
  // código presente, con los mismos nombres, íconos y tonos que el listado de
  // luminarias.
  public htmlResumenEstado(p: IPuesta): string {
    const luminarias = p.luminarias || [];
    const total = luminarias.length;
    if (!total) return '';

    const conteo: Record<number, number> = {
      0: 0,
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    };
    for (const l of luminarias) conteo[calcularEstado(l).codigo]++;

    // Orden de display: encendidas y apagadas primero, luego las problemáticas.
    const orden: CodigoEstadoLuminaria[] = [4, 5, 2, 1, 3, 0];

    let html = `<div class="tabla-celda-col">`;
    html += `<span class="tabla-secundario">${total.toLocaleString('es-AR')} luminaria${total === 1 ? '' : 's'}</span>`;
    for (const codigo of orden) {
      const n = conteo[codigo];
      if (!n) continue;
      const label = presentacionEstado(codigo).label;
      html += `<span>${estadoHtml(estadoLuminariaCelda(codigo, `${n.toLocaleString('es-AR')} ${label}`))}</span>`;
    }
    html += `</div>`;
    return html;
  }

  // Tooltip del resumen: desglose por estado y, sobre todo, el detalle de las
  // luminarias con problema (alarma / error de encendido / error de
  // comunicación) con su identificación y las alarmas snapshot del estado.
  public tooltipResumenEstado(p: IPuesta): string | undefined {
    const luminarias = p.luminarias || [];
    if (!luminarias.length) return undefined;

    const items: string[] = [];

    // Desglose por código (mismo orden que la celda).
    const conteo: Record<number, number> = {
      0: 0,
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    };
    for (const l of luminarias) conteo[calcularEstado(l).codigo]++;
    const orden: CodigoEstadoLuminaria[] = [4, 5, 2, 1, 3, 0];
    for (const codigo of orden) {
      const n = conteo[codigo];
      if (!n) continue;
      const pres = presentacionEstado(codigo);
      items.push(
        `<div><strong>${n.toLocaleString('es-AR')}</strong> ${pres.label}</div>`,
      );
    }

    // Detalle SOLO de alarmas (código 3). Los estados de error de comunicación
    // (1) y de encendido (2) ya se entienden con el desglose de arriba; su
    // "motivo" no aporta y ensucia el tooltip.
    const conAlarma = luminarias.filter((l) => {
      if (calcularEstado(l).codigo !== 3) return false;
      return (l.estado?.alarmas?.filter(Boolean) ?? []).length > 0;
    });
    if (conAlarma.length) {
      items.push(`<div>Alarmas:</div>`);
      for (const l of conAlarma) {
        const label = l.identificacion || l.deveui || 'Luminaria';
        const alarmas = l.estado?.alarmas?.filter(Boolean) ?? [];
        items.push(
          `<div><strong>${escaparHtml(label)}</strong> — ${escaparHtml(alarmas.join(', '))}</div>`,
        );
      }
    }

    return items.length ? items.join('') : undefined;
  }

  // Cálculo del agregado eléctrico de la puesta: suma de potencia (W) y consumo
  // acumulado (kWh) de las luminarias con reporte. Misma extracción por tipo que
  // la columna "Estado eléctrico" del listado de luminarias (Wellness reporta en
  // el periódico; GPE/ACTIS en el reporte de energía). `nAporta` = cuántas
  // luminarias aportaron algún valor (para dejar claro que son sumas).
  private agregadoElectrico(p: IPuesta): {
    potencia: number;
    consumo: number;
    hayP: boolean;
    hayC: boolean;
    nAporta: number;
    total: number;
  } {
    const luminarias = p.luminarias || [];
    let potencia = 0;
    let consumo = 0;
    let hayP = false;
    let hayC = false;
    let nAporta = 0;

    for (const l of luminarias) {
      const tipo = l.dispositivo?.tipo;
      const periodico = l.ultimoReportePeriodico?.valores as any;
      const energia = l.ultimoReporteEnergia?.valores as any;
      let pw: number | undefined;
      let kwh: number | undefined;

      if (tipo === 'Luminaria Wellness') {
        const v = periodico?.voltage;
        const c = periodico?.current;
        pw = v != null && c != null ? v * c : undefined;
        kwh = periodico?.reactivePowerTotal;
      } else if (tipo === 'Luminaria GPE' || tipo === 'Luminaria ACTIS FING') {
        pw = energia?.potencia;
        kwh = energia?.energiaTotal;
      }

      let aporto = false;
      if (pw != null && isFinite(pw)) {
        potencia += pw;
        hayP = true;
        aporto = true;
      }
      if (kwh != null && isFinite(kwh)) {
        consumo += kwh;
        hayC = true;
        aporto = true;
      }
      if (aporto) nAporta++;
    }

    return { potencia, consumo, hayP, hayC, nAporta, total: luminarias.length };
  }

  // Celda del eléctrico total. Rotula explícitamente que es la SUMA de las
  // luminarias de la puesta (con el conteo de cuántas aportaron dato).
  public htmlElectricoTotal(p: IPuesta): string {
    const { potencia, consumo, hayP, hayC, nAporta } =
      this.agregadoElectrico(p);
    if (!hayP && !hayC) return '';

    let inner = `<span class="tabla-secundario">Suma de ${nAporta.toLocaleString('es-AR')} luminaria${nAporta === 1 ? '' : 's'}</span>`;
    if (hayP)
      inner += `<div class="tabla-linea"><span class="material-symbols-outlined">bolt</span><span class="tabla-principal">${numero(potencia)} W</span></div>`;
    if (hayC)
      inner += `<div class="tabla-linea"><span class="material-symbols-outlined">bolt</span><span>${numero(consumo)} kWh acumulados</span></div>`;

    return `<div class="tabla-celda-col">${inner}</div>`;
  }

  public tooltipElectricoTotal(p: IPuesta): string | undefined {
    const { potencia, consumo, hayP, hayC, nAporta, total } =
      this.agregadoElectrico(p);
    if (!hayP && !hayC) return undefined;
    const items: string[] = [
      `<div>Suma de ${nAporta.toLocaleString('es-AR')} de ${total.toLocaleString('es-AR')} luminaria${total === 1 ? '' : 's'} con reporte</div>`,
    ];
    if (hayP)
      items.push(
        `<div>Potencia total: <strong>${numero(potencia)} W</strong></div>`,
      );
    if (hayC)
      items.push(
        `<div>Consumo acumulado total: <strong>${numero(consumo)} kWh</strong></div>`,
      );
    return items.join('');
  }

  // Tipos presentes en la puesta (para mostrar/ocultar comandos por tipo)
  private tieneTipo(p: IPuesta, tipo: string): boolean {
    return (p.luminarias || []).some((l) => l.dispositivo?.tipo === tipo);
  }
  public tieneGPE = (p: IPuesta) => this.tieneTipo(p, this.TIPO_GPE);
  public tieneActis = (p: IPuesta) => this.tieneTipo(p, this.TIPO_ACTIS);
  public tieneWellness = (p: IPuesta) => this.tieneTipo(p, this.TIPO_WELLNESS);

  // ── Primera columna: identificación + dirección/ubicación ─────────────────
  public htmlIdentificacion(p: IPuesta): string {
    const tieneUbicacion = !!(p.ubicacion as IGeoJSONPoint | undefined)
      ?.coordinates?.length;
    let html = `<div class="tabla-celda-col">`;
    html += p.identificacion
      ? `<span class="tabla-principal">${escaparHtml(p.identificacion)}</span>`
      : `<span class="no-info">Sin identificación</span>`;
    // Dirección recortada (la completa va en el tooltip de la columna), igual
    // que el listado de luminarias.
    const direccion = HelperService.extraerDireccion(p.direccion);
    html += direccion
      ? `<div class="tabla-linea"><span class="material-symbols-outlined">place</span><span>${escaparHtml(direccion)}</span></div>`
      : `<span class="tabla-secundario no-info">Sin dirección</span>`;
    if (!tieneUbicacion) {
      html += `<span class="tabla-secundario no-info">Sin ubicación</span>`;
    }
    html += `</div>`;
    return html;
  }

  // ── Última comunicación: la más reciente entre las luminarias de la puesta ─
  private ultimaComunicacionPuesta(
    p: IPuesta,
  ): { fechaMs: number; luminaria: ILuminaria } | null {
    let mejor: { fechaMs: number; luminaria: ILuminaria } | null = null;
    for (const l of p.luminarias || []) {
      const ms = fechaUltimaComunicacion(l);
      if (ms == null) continue;
      if (!mejor || ms > mejor.fechaMs) mejor = { fechaMs: ms, luminaria: l };
    }
    return mejor;
  }

  public htmlUltimaComunicacion(p: IPuesta): string {
    const u = this.ultimaComunicacionPuesta(p);
    if (!u) return '';
    const nombre =
      u.luminaria.identificacion || u.luminaria.deveui || 'Luminaria';
    const fecha = new Date(u.fechaMs);
    return `<div class="tabla-celda-col">
      <span>${fechaCelda(fecha)}</span>
      <span class="tabla-secundario">${haceCelda(fecha)}</span>
      <div class="tabla-linea"><span class="material-symbols-outlined">lightbulb</span><span>${escaparHtml(nombre)}</span></div>
    </div>`;
  }

  // ── Último comando: el más reciente entre los dispositivos de las luminarias ─
  private ultimoComandoPuesta(
    p: IPuesta,
  ): { comando: IComando; luminaria: ILuminaria } | null {
    let mejor: { comando: IComando; luminaria: ILuminaria; ms: number } | null =
      null;
    for (const l of p.luminarias || []) {
      const c = l.dispositivo?.ultimoComando;
      const f = c?.fechaCreacion;
      if (!c || !f) continue;
      const ms = new Date(f).getTime();
      if (isNaN(ms)) continue;
      if (!mejor || ms > mejor.ms) mejor = { comando: c, luminaria: l, ms };
    }
    return mejor
      ? { comando: mejor.comando, luminaria: mejor.luminaria }
      : null;
  }

  // Mismo formato que la columna "Último comando" del listado de luminarias
  // (estado + descripción + fecha), agregando de qué luminaria es.
  public htmlUltimoComando(p: IPuesta): string {
    const u = this.ultimoComandoPuesta(p);
    if (!u) return '';
    const comando = u.comando;

    let inner = `<span>${estadoHtml(this.luminariasService.estadoComandoCelda(comando.estado))}</span>`;
    if (comando.descripcion) {
      inner += `<span>${escaparHtml(comando.descripcion)}</span>`;
    }
    const fecha = comando.fechaActualizacion || comando.fechaCreacion;
    if (fecha) {
      inner += `<span class="tabla-secundario">${fechaCelda(fecha)} · ${haceCelda(fecha)}</span>`;
    }
    const lum = u.luminaria.identificacion || u.luminaria.deveui || 'Luminaria';
    inner += `<div class="tabla-linea"><span class="material-symbols-outlined">lightbulb</span><span>${escaparHtml(lum)}</span></div>`;

    return `<div class="tabla-celda-col">${inner}</div>`;
  }

  public tooltipUltimoComando(p: IPuesta): string | undefined {
    const u = this.ultimoComandoPuesta(p);
    if (!u) return undefined;
    const comando = u.comando;
    const items: string[] = [];
    if (comando.descripcion)
      items.push(`<div>${escaparHtml(comando.descripcion)}</div>`);
    if (comando.estado)
      items.push(
        `<div>Estado: <strong>${escaparHtml(this.luminariasService.estiloEstadoComando(comando.estado).texto)}</strong></div>`,
      );
    items.push(
      `<div>Luminaria: <strong>${escaparHtml(u.luminaria.identificacion || u.luminaria.deveui || 'Luminaria')}</strong></div>`,
    );
    const fecha = comando.fechaActualizacion || comando.fechaCreacion;
    if (fecha) {
      items.push(`<div>Fecha: <strong>${fechaCelda(fecha)}</strong></div>`);
    }
    return items.length ? items.join('') : undefined;
  }

  // ── Asignaciones: grupo(s) de puesta + luminarias que contiene ─────────────
  public htmlAsignaciones(p: IPuesta): string {
    const grupos = (p.grupos || [])
      .map((g) => g?.nombre)
      .filter((n): n is string => !!n);
    const luminarias = (p.luminarias || [])
      .map((l) => l.identificacion || l.deveui)
      .filter((n): n is string => !!n);
    if (!grupos.length && !luminarias.length) return '';
    // El ícono identifica el tipo, el texto es el valor. title para
    // accesibilidad.
    const linea = (icon: string, title: string, valor: string) =>
      `<div class="tabla-linea" title="${title}">
        <span class="material-symbols-outlined">${icon}</span>
        <span>${escaparHtml(valor)}</span>
      </div>`;
    let html = `<div class="tabla-celda-col">`;
    if (grupos.length) {
      html += linea('hub', 'Grupos de puestas', grupos.join(', '));
    }
    if (luminarias.length) {
      html += linea('lightbulb', 'Luminarias', luminarias.join(', '));
    }
    html += `</div>`;
    return html;
  }

  // Tooltip que aclara qué significa cada ícono de la columna Asignaciones.
  // Solo muestra las filas que efectivamente tienen datos.
  public tooltipAsignaciones(p: IPuesta): string | undefined {
    const grupos = (p.grupos || [])
      .map((g) => g?.nombre)
      .filter((n): n is string => !!n);
    const luminarias = (p.luminarias || [])
      .map((l) => l.identificacion || l.deveui)
      .filter((n): n is string => !!n);
    const filas: string[] = [];
    if (grupos.length) {
      filas.push(
        `<div>Grupos de puestas: <strong>${escaparHtml(grupos.join(', '))}</strong></div>`,
      );
    }
    if (luminarias.length) {
      filas.push(
        `<div>Luminarias: <strong>${escaparHtml(luminarias.join(', '))}</strong></div>`,
      );
    }
    return filas.length ? filas.join('') : undefined;
  }

  // Acciones CRUD
  public crear(): void {
    const ref = this.formularios.abrir(CrearEditarPuestaComponent, {
      tamano: 'mediano',
    });
    ref.afterClosed().subscribe(async (res) => {
      if (res) await this.listar();
    });
  }

  public editar(puesta: IPuesta): void {
    const ref = this.formularios.abrir(CrearEditarPuestaComponent, {
      tamano: 'mediano',
      data: { edit: puesta },
    });
    ref.afterClosed().subscribe(async (res) => {
      if (res) await this.listar();
    });
  }

  // Detalle de la puesta: dashboard (solapa Info) + solapas de reportes.
  public verDetalle(puesta: IPuesta): void {
    this.router.navigate(
      ['main/luminarias/detalle-entidad', 'puesta', puesta._id],
      { queryParams: { nombre: puesta.identificacion } },
    );
  }

  // Exporta a Excel las luminarias de esta puesta.
  public async exportar(puesta: IPuesta): Promise<void> {
    const config = await firstValueFrom(
      this.formularios
        .abrir(ExportarLuminariasDialogComponent, {
          tamano: 'chico',
          data: { nivel: 'puesta' },
        })
        .afterClosed(),
    );
    if (!config) return;
    const fecha = new Date().toISOString().slice(0, 10);
    // Job asíncrono: una puesta puede tener muchas luminarias y el mes de
    // eventos/comandos no entra en una request. El panel de Descargas pollea.
    const nombre = `puesta_${puesta.identificacion ?? puesta._id}_${fecha}`;
    this.descargas.agregarDescargaJob(nombre, {
      crearJob: () =>
        this.luminariasHttp.crearExportJob(
          { nivel: 'puesta', id: puesta._id! },
          config,
          `${nombre}.xlsx`,
        ),
      consultarEstado: (jobId) => this.luminariasHttp.getExportJob(jobId),
      descargarArchivo: (jobId) =>
        this.luminariasHttp.descargarExportJob(jobId),
    });
    this.helper.notifSuccess('Exportación iniciada — ver panel de Descargas');
  }

  public async eliminar(puesta: IPuesta): Promise<void> {
    const res = await this.dialogService.confirm(
      'Eliminar puesta',
      `¿Querés eliminar la puesta ${puesta.identificacion}?`,
    );
    if (!res) return;
    try {
      await this.service.delete(puesta._id!);
      this.helper.notifSuccess('Puesta eliminada');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla
  private setColumnas() {
    const puede = () =>
      this.helper.tienePermiso('Luminarias - Enviar comandos');
    const o = (p: IPuesta) => this.objetivo(p); //Objetivo para el envío de comandos
    // SET de un tipo se ocultan si la puesta tiene perfil de ese tipo.
    const ocultaSetGPE = (p: IPuesta) =>
      !this.tieneGPE(p) || this.puestaTienePerfilTipo(p, this.TIPO_GPE);
    const ocultaSetActis = (p: IPuesta) =>
      !this.tieneActis(p) || this.puestaTienePerfilTipo(p, this.TIPO_ACTIS);

    const columnas: IColumnas<IPuesta>[] = [
      {
        header: { label: 'Puesta', sort: 'identificacion', sortable: true },
        row: {
          html: (d) => this.htmlIdentificacion(d),
          tooltip: (d) =>
            d.direccion ? `<div>${escaparHtml(d.direccion)}</div>` : undefined,
        },
      },
      {
        header: { label: 'Luminarias' },
        row: {
          html: (d) => this.htmlResumenEstado(d),
          tooltip: (d) => this.tooltipResumenEstado(d),
          noData: 'Sin luminarias',
        },
      },
      {
        header: { label: 'Última comunicación' },
        row: {
          html: (d) => this.htmlUltimaComunicacion(d),
          noData: 'Sin comunicación',
        },
      },
      {
        header: { label: 'Estado eléctrico (total)' },
        row: {
          html: (d) => this.htmlElectricoTotal(d),
          tooltip: (d) => this.tooltipElectricoTotal(d),
          noData: 'Sin mediciones',
        },
      },
      {
        header: { label: 'Asignaciones' },
        row: {
          html: (d) => this.htmlAsignaciones(d),
          tooltip: (d) => this.tooltipAsignaciones(d),
          noData: 'Sin asignaciones',
        },
      },
    ];

    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: { label: 'Cliente' },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    // Perfil propio (perfiles asignados directamente a esta puesta)
    columnas.push({
      header: { label: 'Perfil propio' },
      row: {
        html: (d) => this.htmlPerfilPropio(d.perfilConfigs),
        noData: 'Sin perfil',
      },
    });

    // Último comando (última columna de datos, antes de Acciones; mismo formato
    // que el listado de luminarias)
    columnas.push({
      header: { label: 'Último comando' },
      row: {
        html: (d) => this.htmlUltimoComando(d),
        tooltip: (d) => this.tooltipUltimoComando(d),
        noData: 'Sin comando',
      },
    });

    columnas.push({
      header: { label: 'Acciones' },
      row: {
        accionesGrouped: [
          {
            tipo: 'material',
            color: 'primary',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (d) => this.verDetalle(d),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (d) => this.editar(d),
            disabled: () =>
              !this.helper.tienePermiso('Luminarias - Editar puestas'),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (d) => this.eliminar(d),
            disabled: () =>
              !this.helper.tienePermiso('Luminarias - Eliminar puestas'),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'account_tree',
            tooltip: 'Ver jerarquía de perfiles',
            click: (d) =>
              this.dialogService.jerarquiaPerfiles({
                nivel: 'puesta',
                id: d._id!,
                nombre: d.identificacion,
              }),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'table_view',
            tooltip: 'Exportar a planilla',
            click: (d) => this.exportar(d),
          },
          // Reaplicar perfil a todas las luminarias de la puesta (GPE + ACTIS).
          // Recupera la config tras un cambio de firmware masivo: el nodo la
          // perdió pero el sistema la sigue creyendo aplicada.
          {
            tipo: 'material',
            color: 'primary',
            icon: 'published_with_changes',
            tooltip: 'Reaplicar perfil',
            disabled: () => !puede(),
            click: (d) => this.comandos.reaplicarPerfil(o(d)),
          },
          // ── Comandos GPE ──
          {
            tipo: 'material',
            icon: 'settings_remote',
            tooltip: 'Comandos GPE',
            oculta: (d) => !this.tieneGPE(d),
            disabled: () => !puede(),
            children: filtrarComandosManuales([
              {
                tipo: 'material',
                color: 'primary',
                icon: 'lightbulb',
                tooltip: 'Encender',
                click: (d) => this.comandos.encenderGPE(o(d)),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'light_off',
                tooltip: 'Apagar',
                click: (d) => this.comandos.apagarGPE(o(d)),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'settings_suggest',
                tooltip: 'Cambiar modo',
                click: (d) => this.comandos.cambiarModoGPE(o(d)),
                oculta: (d) => ocultaSetGPE(d),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'tune',
                tooltip: 'Cambiar configuración',
                click: (d) => this.comandos.createConfigGPE(o(d)),
                oculta: (d) => ocultaSetGPE(d),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'calendar_view_month',
                tooltip: 'Configuración del calendario',
                click: (d) => this.comandos.configCalendarioGPE(o(d)),
                oculta: (d) => ocultaSetGPE(d),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'manage_search',
                tooltip: 'Consultar calendario',
                click: (d) => this.comandos.consultarConfigCalendarioGPE(o(d)),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'manage_search',
                tooltip: 'Consultar configuración',
                click: (d) => this.comandos.consultarConfigGPE(o(d)),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'my_location',
                tooltip: 'Pedir GPS',
                click: (d) => this.comandos.pedirGPSGPE(o(d)),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'power_settings_new',
                tooltip: 'Resetear',
                click: (d) => this.comandos.resetGPE(o(d)),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'power_settings_new',
                tooltip: 'Resetear energía',
                click: (d) => this.comandos.resetEnergiaGPE(o(d)),
                disabled: () => !puede(),
              },
            ]),
          },
          // ── Comandos ACTIS ──
          {
            tipo: 'material',
            icon: 'settings_remote',
            tooltip: 'Comandos ACTIS',
            oculta: (d) => !this.tieneActis(d),
            disabled: () => !puede(),
            children: filtrarComandosManuales([
              {
                tipo: 'material',
                color: 'primary',
                icon: 'lightbulb',
                tooltip: 'Encender',
                click: (d) => this.comandos.encenderActis(o(d)),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'light_off',
                tooltip: 'Apagar',
                click: (d) => this.comandos.apagarActis(o(d)),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'settings_suggest',
                tooltip: 'Cambiar modo',
                click: (d) => this.comandos.cambiarModoActis(o(d)),
                oculta: (d) => ocultaSetActis(d),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'wb_twilight',
                tooltip: 'Configuración astronómica',
                click: (d) => this.comandos.configActis(o(d), 'Offset'),
                oculta: (d) => ocultaSetActis(d),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'wb_sunny',
                tooltip: 'Configuración de fotocélula',
                click: (d) => this.comandos.configActis(o(d), 'Treshold'),
                oculta: (d) => ocultaSetActis(d),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'my_location',
                tooltip: 'Configuración de coordenadas',
                click: (d) => this.comandos.configActis(o(d), 'Coordinates'),
                oculta: (d) => ocultaSetActis(d),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'schedule_send',
                tooltip: 'Configurar reportes',
                click: (d) => this.comandos.configActis(o(d), 'Reporte'),
                oculta: (d) => ocultaSetActis(d),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'verified',
                tooltip: 'Configurar ACK',
                click: (d) => this.comandos.configActis(o(d), 'ACK'),
                oculta: (d) => ocultaSetActis(d),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'manage_search',
                tooltip: 'Consultar configuración',
                click: (d) => this.comandos.consultaActis(o(d), 'config'),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'manage_search',
                tooltip: 'Consultar configuración general',
                click: (d) =>
                  this.comandos.consultaActis(o(d), 'configGeneral'),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'manage_search',
                tooltip: 'Consultar estado',
                click: (d) => this.comandos.consultaActis(o(d), 'estado'),
                disabled: () => !puede(),
              },
            ]),
          },
          // ── Comandos Wellness ──
          {
            tipo: 'material',
            icon: 'settings_remote',
            tooltip: 'Comandos Wellness',
            oculta: (d) => !this.tieneWellness(d),
            disabled: () => !puede(),
            children: [
              {
                tipo: 'material',
                color: 'primary',
                icon: 'lightbulb',
                tooltip: 'Encender',
                click: (d) => this.comandos.encenderWellness(o(d)),
                disabled: () => !puede(),
              },
              {
                tipo: 'material',
                color: 'primary',
                icon: 'light_off',
                tooltip: 'Apagar',
                click: (d) => this.comandos.apagarWellness(o(d)),
                disabled: () => !puede(),
              },
            ],
          },
        ],
      },
    });
    this.columnas = columnas;
  }

  // Hooks
  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  ngOnDestroy() {
    this.puestas$?.unsubscribe();
  }
}
